/**
 * Analytics event taxonomy + capture helpers (Phase 1: reliable engagement).
 *
 * The SDK's job is DUMB, TOTAL CAPTURE: it records generic, structural signals
 * (a click on an actionable element, a scroll milestone, an engagement ping)
 * with a stable identity — and it NEVER decides what a "conversion" is. Meaning
 * is applied later, per-business, as data (a platform webhook, a success-URL
 * rule, or an explicit pick), so the same SDK works on a store, a SaaS app, a
 * clinic, or a content site without knowing anything about the site.
 *
 * This module is the single source of truth for the event NAMES and the
 * (browser-agnostic, node-testable) helpers used to build them. Keep it pure
 * DOM-in / data-out so it can be unit-tested without a full browser.
 */

/**
 * Canonical analytics event names. Bump ANALYTICS_SCHEMA_VERSION when the
 * SHAPE of any of these payloads changes so the ingest side can adapt without
 * guessing.
 *
 * Naming note: the generic click signal is `element_click`, NOT `interaction`
 * — the dashboard's analytics buckets already use `interaction` for the legacy
 * popup-capture type, and reusing it would inflate capture counts.
 */
export const ANALYTICS_EVENTS = {
  SESSION_START: 'session_start',
  SESSION_END: 'session_end',
  PAGE_VIEW: 'page_view',
  ENGAGEMENT_PING: 'engagement_ping',
  SCROLL_MILESTONE: 'scroll_milestone',
  ELEMENT_CLICK: 'element_click',
  FORM_SUBMIT: 'form_submit',
} as const;

/** Version stamped onto every captured payload (`metadata.v`). */
export const ANALYTICS_SCHEMA_VERSION = 1;

/** Scroll depths (percent) we emit a `scroll_milestone` for — once each. */
export const SCROLL_MILESTONES = [25, 50, 75, 100] as const;

/**
 * Thresholds for an "engaged session" — the same idea GA4/Amplitude use, so
 * bounce is simply its inverse: real active time, OR a meaningful scroll, OR
 * any interaction.
 */
export const ENGAGED_ACTIVE_MS = 10_000;
export const ENGAGED_SCROLL_DEPTH = 50;

/**
 * Whether a session counts as engaged from its running metrics. Pure function
 * so the definition lives in exactly one place (SDK, dashboard, and AI can all
 * agree on it later).
 */
export function isEngaged(input: {
  activeMs: number;
  scrollDepth: number;
  interactions: number;
}): boolean {
  return (
    input.activeMs >= ENGAGED_ACTIVE_MS ||
    input.scrollDepth >= ENGAGED_SCROLL_DEPTH ||
    input.interactions > 0
  );
}

/**
 * The elements a click is worth capturing on — the CTAs, links, and buttons
 * that carry intent — not every `<div>` on the page. Keeps event volume sane
 * while still catching every conversion *hint*.
 */
export const ACTIONABLE_SELECTOR =
  'a, button, [role="button"], input[type="submit"], input[type="button"], [data-hamla-cta]';

/**
 * A structural, PII-free description of an element — enough to identify it
 * across visits and to let a human (or the AI) recognise it later, with no
 * user-typed values ever captured.
 */
export interface ElementDescription {
  /** Lowercase tag name, e.g. "a", "button". */
  tag: string;
  /** Visible label (aria-label / text / title / button value), trimmed. */
  text?: string;
  /** ARIA role, if any. */
  role?: string;
  /** Element id, if any. */
  id?: string;
  /** name attribute, if any. */
  name?: string;
  /** Raw href attribute (links only). */
  href?: string;
  /** Absolute destination the click leads to (links only), if resolvable. */
  destination?: string;
  /** A best-effort, reasonably stable CSS selector. */
  selector: string;
}

/**
 * Walk up from an event target to the nearest actionable element. Returns null
 * for clicks that aren't on something interactive. Defensive: tolerates a
 * missing/duck-typed target and never throws.
 */
export function findActionable(target: unknown): Element | null {
  const el = target as { closest?: (s: string) => Element | null } | null;
  if (!el || typeof el.closest !== 'function') return null;
  try {
    return el.closest(ACTIONABLE_SELECTOR);
  } catch {
    return null;
  }
}

/** Resolve an href to an absolute URL when a browser location is available. */
function resolveUrl(href: string | null | undefined): string | undefined {
  if (!href) return undefined;
  try {
    if (typeof window !== 'undefined' && window.location) {
      return new URL(href, window.location.href).href;
    }
  } catch {
    /* fall through to the raw value */
  }
  return href;
}

/** First CSS class of an element, if any. */
function firstClass(el: Element): string | undefined {
  const raw =
    (typeof el.getAttribute === 'function' ? el.getAttribute('class') : null) ||
    (el as { className?: unknown }).className ||
    '';
  const first = String(raw).trim().split(/\s+/).filter(Boolean)[0];
  return first || undefined;
}

/**
 * Build a best-effort stable selector: an id anchor when present, otherwise a
 * short tag[.class] path (max 4 levels). Not guaranteed unique — it's an
 * identity *hint*, not a query — so we keep it cheap and robust rather than
 * perfect.
 */
export function buildSelector(el: Element): string {
  const selfId = (el as { id?: string }).id;
  if (selfId) return `#${selfId}`;

  const parts: string[] = [];
  let node: Element | null = el;
  let depth = 0;
  while (node && depth < 4) {
    const tag = (node.tagName || '').toLowerCase();
    if (!tag || tag === 'html' || tag === 'body') break;

    const id = (node as { id?: string }).id;
    if (id) {
      parts.unshift(`#${id}`);
      break;
    }

    let part = tag;
    const cls = firstClass(node);
    if (cls) part += `.${cls}`;
    parts.unshift(part);

    node = node.parentElement;
    depth++;
  }

  return parts.join(' > ') || (el.tagName || 'element').toLowerCase();
}

/**
 * Describe an actionable element without ever reading user-typed input. `value`
 * is only used as a label for submit/button inputs (where it's the button
 * caption, not user data).
 */
export function describeElement(el: Element): ElementDescription {
  const getAttr = (n: string): string | null =>
    typeof el.getAttribute === 'function' ? el.getAttribute(n) : null;

  const tag = (el.tagName || '').toLowerCase();
  const inputType = tag === 'input' ? (getAttr('type') || '').toLowerCase() : '';
  const safeValue =
    tag === 'input' && ['submit', 'button', 'reset'].includes(inputType)
      ? getAttr('value')
      : null;

  const rawText =
    getAttr('aria-label') ||
    (el.textContent || '').trim() ||
    getAttr('title') ||
    safeValue ||
    undefined;
  const text = rawText ? rawText.replace(/\s+/g, ' ').slice(0, 120) : undefined;

  const hrefAttr = getAttr('href');

  return {
    tag,
    text,
    role: getAttr('role') || undefined,
    id: (el as { id?: string }).id || undefined,
    name: getAttr('name') || undefined,
    href: hrefAttr || undefined,
    destination: resolveUrl(hrefAttr),
    selector: buildSelector(el),
  };
}
